import { memo } from 'react';

import { createColumnHelper, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { ScrollableCell, ScrollableCellWithAvatar } from '@/components/data-table/cells';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';
import { getMemberStatusText, StatusBadge, StatusBadgeType } from '@/modules/common';
import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { ReservedUserGroupName } from '@/modules/common/types/reserved-user-group-name';
import { UserGroup } from '@/modules/common/types/user-group';
import { MemberStatus } from '@/modules/team/types/team-member';

import { useMemberEditsStore } from '../store/member-edits.store';
import { MemberAccessRow } from '../types/member-access';
import { getInviteDisabledKey, isEmailValid, isInvitable } from '../utils/member-email';

const columnHelper = createColumnHelper<MemberAccessRow>();

interface GetMemberAccessColumnsParams {
  groupOptions: SingleSelectOption[];
  isInviteMode: boolean;
  defaultGroup: UserGroup | null;
}

// ---------------------------------------------------------------------------
// Group cell — store-driven base Select (no RHF). Subscribes only to its own
// userId's groupId slice so a single change re-renders just this cell.
// ---------------------------------------------------------------------------
interface GroupCellProps {
  userId: number;
  options: SingleSelectOption[];
  /** Options excluding Super Admin (used unless the member already IS a super admin). */
  tableOptions: SingleSelectOption[];
  superAdminId?: string;
  /** Shown when the row has no group of its own — that is what an invite would actually apply. */
  defaultGroupName?: string;
}

const GroupCell = memo(function GroupCell({
  userId,
  options,
  tableOptions,
  superAdminId,
  defaultGroupName,
}: GroupCellProps) {
  const groupId = useMemberEditsStore(state => state.edits[userId]?.groupId ?? null);
  const setGroupId = useMemberEditsStore(state => state.setGroupId);
  // Remount the (controlled) Select on a wholesale reset/rebaseline so it always
  // reflects the restored value regardless of Radix's controlled-value handling.
  const version = useMemberEditsStore(state => state.version);

  const isSuperAdmin = groupId === superAdminId;
  const selectOptions = isSuperAdmin ? options : tableOptions;

  return (
    <Select
      key={version}
      value={groupId ?? undefined}
      onValueChange={value => setGroupId(userId, value)}
      disabled={isSuperAdmin}
    >
      <SelectTrigger className="w-full">
        {/* Rendered as the placeholder, not a real selection: the row keeps a null group (so
            saving group edits never assigns the default to members nobody invited) while still
            showing what an invite would apply. */}
        <SelectValue
          placeholder={
            defaultGroupName
              ? i18n.t('authenticated.settings:pages.members.defaultGroupOption', {
                  name: defaultGroupName,
                })
              : i18n.t('authenticated.settings:pages.members.selectGroup')
          }
        />
      </SelectTrigger>
      <SelectContent>
        {selectOptions.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
});

// ---------------------------------------------------------------------------
// Email cell — store-driven Input for Basic members; read-only otherwise.
// Inline error shows only for a non-empty, invalid email (mirrors the zod rule
// `z.string().email().or(z.literal(''))`).
// ---------------------------------------------------------------------------
interface EmailCellProps {
  userId: number;
}

const EmailCell = memo(function EmailCell({ userId }: EmailCellProps) {
  const email = useMemberEditsStore(state => state.edits[userId]?.email ?? null);
  const setEmail = useMemberEditsStore(state => state.setEmail);
  // Remount on wholesale reset/rebaseline so the field reverts to the restored value.
  const version = useMemberEditsStore(state => state.version);

  const hasError = !!email && email.trim() !== '' && !isEmailValid(email);

  return (
    <div className="w-full">
      <Input
        key={version}
        value={email ?? ''}
        onChange={event => setEmail(userId, event.target.value)}
        type="email"
        placeholder={i18n.t('authenticated.settings:pages.members.emailPlaceholder')}
        className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Selection cell — store-driven checkbox + tooltip. Subscribes to its own
// groupId/email/selected slices. The tooltip wraps an ENABLED element (the
// label/span) because a disabled Checkbox emits no hover events.
// ---------------------------------------------------------------------------
interface SelectCellProps {
  userId: number;
  hasDefaultGroup: boolean;
}

const SelectCell = memo(function SelectCell({ userId, hasDefaultGroup }: SelectCellProps) {
  const { t } = useTranslation('authenticated.settings');
  const groupId = useMemberEditsStore(state => state.edits[userId]?.groupId ?? null);
  const email = useMemberEditsStore(state => state.edits[userId]?.email ?? null);
  const selected = useMemberEditsStore(state => state.edits[userId]?.selected ?? false);
  const setSelected = useMemberEditsStore(state => state.setSelected);

  const invitable = isInvitable({ groupId, email, hasDefaultGroup });
  const disabledKey = getInviteDisabledKey({ groupId, email, hasDefaultGroup });
  const disabledReason = disabledKey ? t(disabledKey) : undefined;

  const control = (
    <label className="flex w-full cursor-pointer items-center justify-center py-4">
      <Checkbox
        checked={selected}
        disabled={!invitable}
        onCheckedChange={value => setSelected(userId, !!value)}
        aria-label="Select row"
      />
    </label>
  );

  if (!invitable && disabledReason) {
    return <SimpleTooltip content={disabledReason}>{control}</SimpleTooltip>;
  }

  return control;
});

// ---------------------------------------------------------------------------
// Select-all header — store-only. Reads currently-loaded selectable rows from
// the table; computes invitable per row from the live store inside the handler
// (never in render, to avoid loops).
// ---------------------------------------------------------------------------
interface SelectAllHeaderProps {
  table: Table<MemberAccessRow>;
  hasDefaultGroup: boolean;
}

const SelectAllHeader = memo(function SelectAllHeader({
  table,
  hasDefaultGroup,
}: SelectAllHeaderProps) {
  // Subscribe so the header re-renders when selection or edits change.
  const edits = useMemberEditsStore(state => state.edits);
  const setSelectedMany = useMemberEditsStore(state => state.setSelectedMany);

  const loadedRows = table.getRowModel().rows;
  const invitableIds: number[] = [];
  for (const row of loadedRows) {
    const edit = edits[row.original.userId];
    if (edit && isInvitable({ groupId: edit.groupId, email: edit.email, hasDefaultGroup }))
      invitableIds.push(row.original.userId);
  }

  const selectedCount = invitableIds.filter(id => edits[id]?.selected).length;
  const allSelected = invitableIds.length > 0 && selectedCount === invitableIds.length;
  const someSelected = selectedCount > 0;

  return (
    <label className="flex w-full cursor-pointer items-center justify-center">
      <Checkbox
        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
        disabled={invitableIds.length === 0}
        onCheckedChange={checked => setSelectedMany(invitableIds, !!checked)}
        aria-label="Select all"
      />
    </label>
  );
});

export const getMemberAccessColumns = ({
  groupOptions,
  isInviteMode,
  defaultGroup,
}: GetMemberAccessColumnsParams) => {
  const superAdminOption = groupOptions.find(
    option => option.label === ReservedUserGroupName.SUPER_ADMIN
  );
  const superAdminId = superAdminOption?.value;

  const tableGroupOptions = superAdminId
    ? groupOptions.filter(option => option.value !== superAdminId)
    : groupOptions;

  const selectColumn = columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <SelectAllHeader table={table} hasDefaultGroup={defaultGroup !== null} />
    ),
    cell: ({ row }) => (
      <SelectCell userId={row.original.userId} hasDefaultGroup={defaultGroup !== null} />
    ),
    size: 50,
    minSize: 40,
    enableResizing: false,
  });

  return [
    ...(isInviteMode ? [selectColumn] : []),
    columnHelper.accessor('name', {
      header: i18n.t('authenticated.team:name'),
      cell: ({ row, getValue }) => (
        <ScrollableCellWithAvatar
          text={getValue() || ''}
          avatarSize="sm"
          image={row.original?.profileImageUrl}
          avatarName={getValue()}
          className={cn(row.original.status === MemberStatus.Archived && 'opacity-50')}
        />
      ),
      size: 200,
      minSize: 150,
      enablePinning: true,
    }),
    columnHelper.accessor('email', {
      header: i18n.t('authenticated.team:email'),
      size: 260,
      minSize: 180,
      cell: ({ getValue, row }) => {
        const isEditable = row.original.status === MemberStatus.Basic;

        if (!isEditable) {
          return (
            <ScrollableCell
              text={getValue() ?? ''}
              className={cn(row.original.status === MemberStatus.Archived && 'opacity-50')}
            />
          );
        }

        return <EmailCell userId={row.original.userId} />;
      },
    }),
    columnHelper.accessor('groupId', {
      header: i18n.t('authenticated.settings:pages.members.groupColumn'),
      cell: ({ row }) => (
        <GroupCell
          userId={row.original.userId}
          options={groupOptions}
          tableOptions={tableGroupOptions}
          superAdminId={superAdminId}
          defaultGroupName={defaultGroup?.name}
        />
      ),
      size: 300,
      minSize: 220,
    }),
    columnHelper.accessor('status', {
      header: () => (
        <div className="flex w-full justify-center">
          {i18n.t('authenticated.team:status.label')}
        </div>
      ),
      meta: { cellClassName: 'text-center' },
      cell: ({ getValue }) => (
        <StatusBadge
          type={StatusBadgeType.TeamMember}
          status={getValue()}
          label={getMemberStatusText(getValue())}
        />
      ),
      size: 140,
      minSize: 120,
    }),
  ];
};
